'use client';

import { Footer, Header } from '@/components';
import { YotpoReferrals } from '@/components/common';
import useAuth from '@/context/AuthContext';
import {
  TypeFooter,
  TypeHeader,
  TypePage,
  TypeSubFooter,
} from '@/core/generated-types';
import { useNavigationEvent } from '@/hooks/useNavigationEvent';
import { useTrustedShopsScript } from '@/hooks/useTrustedShopsScript';
import { getLeiaDeviceId } from '@/utils/leiaClientSide';
import { identifyCurrentUser, pageEvent } from '@/utils/rudderstack';
import { trackGuestCustomer, trackLoggedInCustomer } from '@/utils/dataLayer';
import Head from 'next/head';
import React, { useEffect } from 'react';
import { NewRelicSnippet } from '../NewRelicSnippet/NewRelicSnippet';
import Cookies from 'js-cookie';

interface LayoutInterface {
  children: React.ReactNode;
  page: TypePage;
  header: TypeHeader;
  footer: TypeFooter;
  footerLegal: TypeSubFooter;
}

export const RETURN_URL_KEY = 'lovevery_return_url';

const handleReturnUrl = (event: Event) => {
  const target = (event.target as HTMLAnchorElement).closest('a');
  if (!target?.href.includes('/account/login')) {
    return;
  }

  const { hostname, href } = window.location;
  const domain =
    hostname !== 'localhost'
      ? process.env.NEXT_PUBLIC_VANITY_DOMAIN
      : undefined;

  Cookies.set(RETURN_URL_KEY, encodeURIComponent(href), {
    domain,
    path: '/',
    sameSite: 'Strict',
    secure: true,
  });
};

const Layout = ({
  children,
  page,
  header,
  footer,
  footerLegal,
}: LayoutInterface) => {
  const { user, authLoading } = useAuth();
  const leia = getLeiaDeviceId();
  // This Contentful field works together with EnterPasswordForm from lovevery-react
  const returnAfterLogin = page?.fields?.returnAfterLogin;

  useEffect(() => {
    if (!authLoading) {
      if (user && user.attributes) {
        // Idenitfy user in rudderstack if the user is logged in
        identifyCurrentUser(user);
        trackLoggedInCustomer(user, leia);
      } else {
        trackGuestCustomer(leia);
      }
      pageEvent();
    }
  }, [authLoading, user, user?.attributes, leia]);

  useNavigationEvent(() => {
    pageEvent();
  });

  useTrustedShopsScript();

  useEffect(() => {
    if (!returnAfterLogin) {
      return;
    }
    document.addEventListener('click', handleReturnUrl);
    return () => {
      document.removeEventListener('click', handleReturnUrl);
    };
  }, [returnAfterLogin]);

  return (
    <>
      <Head>
        <NewRelicSnippet />
        <meta
          name="viewport"
          content="width=device-width, height=device-height,initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
      </Head>
      <Header header={header} />
      <YotpoReferrals />

      <main data-testid="le-main" id="main-layout">
        {children}
      </main>
      <Footer footer={footer} footerLegal={footerLegal} />
    </>
  );
};

export default Layout;
